import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import SeoHead from "@/components/SeoHead";
import NotFound from "@/pages/NotFound";
import PageHero from "@/components/PageHero";
import { SectionBand, SectionHeader, ArrowLink } from "@/components/SectionPrimitives";
import { CAPABILITIES } from "@/lib/dataleo";
import { serviceUrl } from "@/lib/servicePaths";
import { EDITORIAL_PHOTOS } from "@/lib/editorialPhotos";
import { CAPABILITY_ICONS } from "@/components/CapabilityIcons";

// -----------------------------------------------------------------------
// Capabilities landing - refonte 2026 (Citwell style).
// Hero cream + hero photo, then each pillar as a FULL BAND alternating
// cream / dark. Each pillar reads like a page section with clusters as
// stacked rows (not a card grid).
// -----------------------------------------------------------------------

const PILLAR_PHOTOS = {
  advisory: EDITORIAL_PHOTOS.spotStrategy,
  delivery: EDITORIAL_PHOTOS.spotDashboard,
  "ai-build": EDITORIAL_PHOTOS.spotIa,
};

// Pillar accent card - custom line-art icon on a flat Dataleo background.
// Replaces generic stock photos with a boutique signature per capability.
const PillarAccent = ({ slug, isDark }) => {
  const Icon = CAPABILITY_ICONS[slug];
  if (!Icon) return null;
  const bg = isDark ? "bg-[#0A1612]" : "bg-white";
  const border = isDark ? "border-[#1F2A26]" : "border-[#E6E0D5]";
  const dot = isDark ? "bg-[#FFD400]" : "bg-[#00A878]";
  const stroke = isDark ? "text-[#FFD400]" : "text-[#00A878]";
  return (
    <div className={`relative aspect-square max-w-[320px] rounded-[24px] overflow-hidden border ${border} ${bg} hidden sm:block`} data-testid={`pillar-icon-${slug}`}>
      {/* Grid texture - faint lines for a technical, APS-console feel */}
      <svg aria-hidden="true" className={`absolute inset-0 w-full h-full ${isDark ? "text-white/[0.04]" : "text-[#0B1210]/[0.05]"}`} viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <pattern id={`grid-${slug}`} width="12.5" height="12.5" patternUnits="userSpaceOnUse">
            <path d="M 12.5 0 L 0 0 0 12.5" fill="none" stroke="currentColor" strokeWidth="0.4" />
          </pattern>
        </defs>
        <rect width="100" height="100" fill={`url(#grid-${slug})`} />
      </svg>
      {/* Corner mark */}
      <div className={`absolute top-5 left-5 w-10 h-[2px] rounded-full ${dot}`} aria-hidden="true" />
      {/* Icon - centered */}
      <div className={`absolute inset-10 ${stroke}`}>
        <Icon />
      </div>
    </div>
  );
};

export const Capabilities = () => {
  const { lang } = useLang();
  return (
    <div data-testid="capabilities-page">
      <SeoHead
        lang={lang}
        path="/capabilities"
        title={lang === "fr" ? "Nos capacités - Dataleo" : "Our capabilities - Dataleo"}
        description={lang === "fr"
          ? "Advisory, Delivery, IA & Decision Build : trois capacités Dataleo pour construire de meilleurs systèmes de décision Supply Chain."
          : "Advisory, Delivery, AI & Decision Build: three Dataleo capabilities to build better Supply Chain decision systems."}
      />
      <PageHero
        tone="cream"
        kicker={lang === "fr" ? "Nos capacités" : "Our capabilities"}
        title={lang === "fr" ? "De la stratégie au RUN." : "From strategy to RUN."}
        description={lang === "fr"
          ? "Trois capacités complémentaires - mobilisées séparément ou en système - pour concevoir, livrer et gouverner vos décisions Supply Chain."
          : "Three complementary capabilities - mobilised separately or as one system - to design, deliver and govern your Supply Chain decisions."}
        primaryCta={{ to: "/contact", label: lang === "fr" ? "En parler avec Dataleo" : "Talk to Dataleo" }}

        testId="capabilities-hero"
      />

      {CAPABILITIES.map((p, i) => {
        const tone = i % 2 === 0 ? "cream" : "dark";
        const isDark = tone === "dark";
        return (
          <SectionBand key={p.slug} tone={tone} testId={`cap-${p.slug}`}>
            <div className="grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-10 lg:gap-16 items-start">
              <div>
                <div className="font-mono-dl text-[11px] tracking-[0.22em] uppercase text-[#00A878]">
                  0{i + 1} · {p.tag}
                </div>
                <h2 className={`font-display text-5xl sm:text-6xl mt-5 leading-[0.95] ${isDark ? "text-white" : "text-[#0B1210]"}`}>
                  {lang === "fr" ? p.title.fr : p.title.en}
                </h2>
                <p className={`font-display italic text-2xl mt-4 ${isDark ? "text-[#FFD400]" : "text-[#00A878]"}`} style={{ fontWeight: 700, letterSpacing: "-0.015em" }}>
                  {lang === "fr" ? p.tagline.fr : p.tagline.en}
                </p>
                <div className="mt-8">
                  <PillarAccent slug={p.slug} isDark={isDark} />
                </div>
                <div className="mt-8">
                  <Link
                    to={`/capabilities/${p.slug}`}
                    data-testid={`cap-link-${p.slug}`}
                    className={`dl-pill ${isDark ? "dl-pill-primary" : "dl-pill-dark"}`}
                  >
                    {lang === "fr" ? "Ouvrir la fiche" : "Open capability"}
                    <ArrowRight size={15} strokeWidth={2.5} />
                  </Link>
                </div>
              </div>
              <div>
                <p className={`text-[16px] sm:text-[17px] leading-[1.7] ${isDark ? "text-white/75" : "text-[#4B5563]"}`}>
                  {lang === "fr" ? p.intro.fr : p.intro.en}
                </p>
                <ul className={`mt-10 divide-y ${isDark ? "divide-[#1F2A26]" : "divide-[#E6E0D5]"} border-y ${isDark ? "border-[#1F2A26]" : "border-[#E6E0D5]"}`}>
                  {p.clusters.map((c) => (
                    <li key={c.code}>
                      <Link
                        to={`/capabilities/${p.slug}#cluster-${c.code}`}
                        className={`group grid grid-cols-[48px_1fr_auto] items-center gap-4 py-5 min-h-[64px] transition-colors ${isDark ? "hover:bg-white/5" : "hover:bg-white"}`}
                      >
                        <span className={`font-mono-dl text-[11px] tracking-widest ${isDark ? "text-white/40" : "text-[#9ca3af]"}`}>
                          {c.code}
                        </span>
                        <span className={`font-display text-xl sm:text-2xl leading-tight ${isDark ? "text-white group-hover:text-[#FFD400]" : "text-[#0B1210] group-hover:text-[#00A878]"} transition-colors`}>
                          {lang === "fr" ? c.name.fr : c.name.en}
                        </span>
                        <span className={`font-mono-dl text-[10px] uppercase tracking-widest ${isDark ? "text-white/40" : "text-[#4B5563]"}`}>
                          {c.items.length} {lang === "fr" ? "axes" : "topics"}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </SectionBand>
        );
      })}
    </div>
  );
};

// -----------------------------------------------------------------------
// Capability detail - premium long-form.
// -----------------------------------------------------------------------
const ClusterSection = ({ cluster, lang, variant }) => (
  <section
    id={`cluster-${cluster.code}`}
    data-testid={`cluster-section-${cluster.code}`}
    className="scroll-mt-40 py-14 border-t border-[#E6E0D5] first:border-t-0"
  >
    <div className="flex items-baseline gap-5 mb-8">
      <span className="font-display text-6xl text-[#00A878] leading-none">{cluster.code}</span>
      <div>
        <div className="font-mono-dl text-[11px] tracking-[0.22em] uppercase text-[#00A878]">
          {lang === "fr" ? "Cluster" : "Cluster"}
        </div>
        <h2 className="font-display text-3xl sm:text-4xl text-[#0B1210] leading-tight mt-2">
          {lang === "fr" ? cluster.name.fr : cluster.name.en}
        </h2>
      </div>
    </div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 pl-0 sm:pl-20">
      {cluster.items.map((it) => {
        const link = serviceUrl(variant.pillarSlug, it);
        return (
          <Link
            key={it}
            to={link}
            data-testid={`cluster-item-${cluster.code}-${it.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`group relative px-5 py-4 text-[14px] font-bold-dl rounded-full transition-all flex items-center gap-2 ${
              variant.type === "aps"
                ? "border-2 border-[#00A878] text-[#00A878] bg-white hover:bg-[#00A878] hover:text-white"
                : "border-2 border-[#E6E0D5] text-[#0B1210] bg-white hover:border-[#0B1210]"
            }`}
          >
            <span className="flex-1">{it}</span>
            <ArrowRight size={13} className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0" aria-hidden="true" />
          </Link>
        );
      })}
    </div>
    <div className="pl-0 sm:pl-20 mt-4">
      <span className="font-mono-dl text-[10px] tracking-widest uppercase text-[#9ca3af]">
        {lang === "fr" ? "Chaque expertise mène à sa propre page de service." : "Explore a dedicated page for each service."}
      </span>
    </div>
  </section>
);

const ClusterNav = ({ clusters, lang }) => {
  const scrollTo = (e, code) => {
    e.preventDefault();
    const el = document.getElementById(`cluster-${code}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <div data-testid="cluster-nav" className="sticky top-[6.75rem] z-30 bg-[#F6F3EE] border-b border-[#E6E0D5]">
      <div className="dl-container flex items-center gap-6 overflow-x-auto py-3 no-scrollbar">
        <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563] shrink-0">
          {lang === "fr" ? "Clusters" : "Clusters"}
        </span>
        {clusters.map((c) => (
          <a
            key={c.code}
            href={`#cluster-${c.code}`}
            onClick={(e) => scrollTo(e, c.code)}
            data-testid={`cluster-nav-${c.code}`}
            className="text-[13px] font-bold-dl text-[#4B5563] hover:text-[#00A878] whitespace-nowrap transition-colors min-h-[40px] inline-flex items-center gap-1.5"
          >
            <span className="font-mono-dl text-[10px] text-[#9ca3af]">{c.code}</span>
            {lang === "fr" ? c.name.fr : c.name.en}
          </a>
        ))}
      </div>
    </div>
  );
};

export const Capability = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const pillar = CAPABILITIES.find((p) => p.slug === slug);

  useEffect(() => {
    if (!pillar) return;
    const id = window.location.hash.replace(/^#/, "");
    if (!id) return;
    const el = document.getElementById(id);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  }, [pillar, slug]);

  if (!pillar) return <NotFound />;

  const title = lang === "fr" ? pillar.title.fr : pillar.title.en;
  const tagline = lang === "fr" ? pillar.tagline.fr : pillar.tagline.en;
  const intro = lang === "fr" ? pillar.intro.fr : pillar.intro.en;

  return (
    <div data-testid={`capability-${slug}`}>
      <SeoHead lang={lang} path={`/capabilities/${slug}`} title={`${title} - Dataleo`} description={intro} />

      <PageHero
        tone="cream"
        kicker={pillar.tag}
        title={title}
        description={intro}
        testId={`capability-hero-${slug}`}
        primaryCta={{ to: "/contact", label: lang === "fr" ? "En parler avec Dataleo" : "Talk to Dataleo" }}
      />

      <ClusterNav clusters={pillar.clusters} lang={lang} />

      <div className="dl-container pb-20">
        {pillar.clusters.map((c) => (
          <ClusterSection key={c.code} cluster={c} lang={lang} variant={{ type: c.variant, pillarSlug: pillar.slug }} />
        ))}
      </div>

      {/* Delivery pillar: "From transformation to RUN." band */}
      {pillar.run && (
        <SectionBand tone="dark">
          <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-10 items-start">
            <div>
              <div className="font-mono-dl text-[11px] tracking-[0.22em] uppercase text-[#00A878]">RUN</div>
              <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight text-white">
                {lang === "fr" ? pillar.run.title.fr : pillar.run.title.en}
              </h2>
              <p className="font-display text-2xl italic text-[#FFD400] mt-5" style={{ fontWeight: 700 }}>
                "{lang === "fr" ? pillar.run.tagline.fr : pillar.run.tagline.en}"
              </p>
              <p className="mt-6 text-[15px] text-white/70 max-w-md leading-relaxed">
                {lang === "fr" ? pillar.run.body.fr : pillar.run.body.en}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {pillar.run.items.map((it) => (
                <div key={it} className="border border-[#1F2A26] bg-[#0A1612] rounded-full px-4 py-3 text-[13px] text-white font-bold-dl text-center">
                  {it}
                </div>
              ))}
            </div>
          </div>
        </SectionBand>
      )}

      {/* AI Build principle strip */}
      {pillar.principle && (
        <section className="bg-[#00A878] text-white">
          <div className="dl-container py-14 grid md:grid-cols-[minmax(0,1fr)_auto] gap-6 items-baseline">
            <p className="font-display text-4xl sm:text-5xl leading-tight">
              {lang === "fr" ? pillar.principle.fr : pillar.principle.en}
            </p>
            <div className="font-mono-dl text-[11px] tracking-[0.22em] uppercase text-white/80 shrink-0">
              {lang === "fr" ? "Principe" : "Principle"}
            </div>
          </div>
          {pillar.supportingStatement && (
            <div className="dl-container pb-10 -mt-2 text-[15px] italic text-white/80">
              {lang === "fr" ? pillar.supportingStatement.fr : pillar.supportingStatement.en}
            </div>
          )}
        </section>
      )}

      <SectionBand tone="cream">
        <div className="flex flex-wrap gap-3">
          <Link to="/contact" className="dl-pill dl-pill-primary">
            {lang === "fr" ? "En parler avec Dataleo" : "Talk to Dataleo"}
            <ArrowRight size={15} strokeWidth={2.5} />
          </Link>
          <Link to="/capabilities" className="dl-pill dl-pill-outline">
            {lang === "fr" ? "Toutes les capacités" : "All capabilities"}
            <ArrowRight size={15} strokeWidth={2.5} />
          </Link>
        </div>
      </SectionBand>
    </div>
  );
};
